import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, ShoppingCart, LogOut, Menu, X, User,
  RefreshCw, Database, Users, Warehouse,
  Truck, RotateCcw, BarChart3, ChevronDown, FileDown, Camera, Trash2, Mail, Tag, Package, Ruler
} from 'lucide-react';
import { Button } from './ui/button';

const ADMIN_NAV = [
  { type: 'link', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { type: 'link', path: '/orders', label: 'Orders', icon: ShoppingCart },
  { type: 'link', path: '/admin/custom-orders', label: 'Custom Orders', icon: Ruler },
  { type: 'link', path: '/reports', label: 'Reports', icon: BarChart3 },
  { type: 'group', label: 'Master Data', icon: Database, items: [
    { path: '/master-data/products', label: 'Product Master', icon: Database },
    { path: '/master-data/customers', label: 'Customer Master', icon: Users },
    { path: '/master-data/warehouses', label: 'Warehouse Master', icon: Warehouse },
  ]},
  { type: 'link', path: '/admin/subscriptions', label: 'Subscriptions', icon: RefreshCw },
  { type: 'link', path: '/admin/promo-codes', label: 'Promo Codes', icon: Tag },
  { type: 'link', path: '/refund-log', label: 'Refund Log', icon: RotateCcw },
  { type: 'link', path: '/admin/email-activity', label: 'Email Activity', icon: Mail },
  { type: 'link', path: '/admin/settings', label: 'Settings', icon: LayoutDashboard },
  { type: 'link', path: '/user-management', label: 'User Management', icon: Users },
  { type: 'link', path: '/data-management', label: 'Data Management', icon: Trash2 },
];

const CUSTOMER_NAV = [
  { type: 'link', path: '/customer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { type: 'link', path: '/customer/orders', label: 'My Orders', icon: ShoppingCart },
  { type: 'link', path: '/customer/new-order', label: 'New Order', icon: Package },
  { type: 'link', path: '/customer/invoices', label: 'My Invoices', icon: FileDown },
  { type: 'link', path: '/customer/track', label: 'Track Order', icon: Truck },
  { type: 'link', path: '/customer/subscriptions', label: 'Subscriptions', icon: RefreshCw },
  { type: 'link', path: '/customer/profile', label: 'My Profile', icon: User },
];

const DELIVERY_NAV = [
  { type: 'link', path: '/delivery', label: 'Delivery Portal', icon: Truck },
];

const NavLink = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
      className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm
        ${isActive
          ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-md'
          : 'text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50'
        }`}
    >
      <Icon size={18} />
      <span className="font-medium">{item.label}</span>
    </Link>
  );
};

const NavGroup = ({ group, pathname, onClick }) => {
  const Icon = group.icon;
  const isChildActive = group.items.some(i => pathname === i.path);
  const [open, setOpen] = useState(isChildActive);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 text-sm
          ${isChildActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
      >
        <div className="flex items-center space-x-3">
          <Icon size={18} />
          <span className="font-medium">{group.label}</span>
        </div>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-100 pl-3">
          {group.items.map(item => (
            <NavLink key={item.path} item={item} isActive={pathname === item.path} onClick={onClick} />
          ))}
        </div>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navConfig = isAdmin ? ADMIN_NAV : (user?.role === 'delivery' ? DELIVERY_NAV : CUSTOMER_NAV);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="mr-4 lg:hidden">
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <Link to={isAdmin ? '/dashboard' : (user?.role === 'delivery' ? '/delivery' : '/customer/dashboard')} className="flex items-center space-x-3">
                <img
                  src="/at1s-logo-solid.png"
                  alt="AirTech OneSolutions Logo"
                  className="h-12 w-auto object-contain"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="hidden md:flex items-center space-x-2">
                Home
              </Button>
              <div className="hidden md:flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <User size={18} className="text-blue-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.role === 'admin' ? 'Administrator' : user?.role === 'delivery' ? 'Delivery' : user?.company || 'Customer'}</p>
                  </div>
                </div>
              </div>
              <Button onClick={handleLogout} variant="outline" size="sm" className="flex items-center space-x-2">
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] z-30
          bg-white border-r border-gray-200 shadow-lg lg:shadow-none
          transition-transform duration-300 ease-in-out overflow-y-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} w-64`}>
          <nav className="p-3 space-y-1">
            {navConfig.map((item, idx) =>
              item.type === 'group' ? (
                <NavGroup key={idx} group={item} pathname={location.pathname} onClick={closeSidebar} />
              ) : (
                <NavLink key={item.path} item={item} isActive={location.pathname === item.path} onClick={closeSidebar} />
              )
            )}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-8 pb-16">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={closeSidebar} />
      )}
    </div>
  );
};

export default Layout;
